if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // CUSTOM CODE: Filter images on initial load
        const activeMedia = this.elements.viewer.querySelector('.is-active');
        if (activeMedia) {
          this.filterMediaByAlt(activeMedia.dataset.alt);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // CUSTOM CODE: Filter images whenever media changes
        // We pass the active image's alt text to the filter function
        if (activeMedia.dataset.alt) {
           this.filterMediaByAlt(activeMedia.dataset.alt);
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // ---------------------------------------------------------
      // CUSTOM CODE: Flexible "Prefix + Variant" Filtering Logic
      // ---------------------------------------------------------
      filterMediaByAlt(activeAlt) {
        // CONFIGURATION: Set your desired prefix here (Case Insensitive)
        const VARIANT_PREFIX = "Color: "; 
        
        if (!activeAlt) return; 

        const normalize = (str) => (str ? str.trim().toLowerCase() : '');
        const targetAlt = normalize(activeAlt);
        const prefix = normalize(VARIANT_PREFIX);

        // 1. Determine the "Filter Token"
        // We look for the Prefix + Variant Name inside the Active Image's Alt text.
        // It captures everything after the prefix until a comma or the end of the string.
        let filterToken = "";

        if (targetAlt.includes(prefix)) {
            // Escape special regex characters in prefix if needed, then build regex
            // This Regex looks for: "Color: " followed by anything NOT a comma
            const regex = new RegExp(`${prefix}[^,]+`, 'i');
            const match = targetAlt.match(regex);
            
            if (match) {
                filterToken = match[0].trim(); // e.g., "color: dark brown"
            }
        }

        // If we couldn't find the prefix in the active image, we stop and do nothing (or show all).
        // Safest behavior: If the active image doesn't have the tag, show everything (reset).
        if (!filterToken) {
            this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => el.style.display = '');
            if (this.elements.thumbnails) {
                this.elements.thumbnails.querySelectorAll('[data-target]').forEach(el => el.style.display = '');
            }
            return;
        }

        const toggleVisibility = (elements) => {
          elements.forEach((el) => {
            const elAlt = normalize(el.dataset.alt);
            
            // SHOW IF:
            // 1. Alt is empty (Generic images like size charts)
            // 2. Alt contains the specific "Color: Variant" token we found
            if (elAlt === '' || elAlt.includes(filterToken)) {
              el.style.display = '';
            } else {
              el.style.display = 'none';
            }
          });
        };

        // Filter main gallery items
        toggleVisibility(this.elements.viewer.querySelectorAll('[data-media-id]'));

        // Filter thumbnails
        if (this.elements.thumbnails) {
           toggleVisibility(this.elements.thumbnails.querySelectorAll('[data-target]'));
        }
      }
      // ---------------------------------------------------------

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}